import {
  Alert,
  Box,
  Button,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { describeError } from '../services/api';
import { useAuthStore } from '../store/auth.store';

export default function LoginPage() {
  const status = useAuthStore((state) => state.status);
  const mustCreateAdmin = useAuthStore((state) => state.mustCreateAdmin);
  const login = useAuthStore((state) => state.login);
  const setupAdmin = useAuthStore((state) => state.setupAdmin);
  const bootstrap = useAuthStore((state) => state.bootstrap);
  const navigate = useNavigate();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // A cookie session may already be live (e.g. after a refresh).
  useEffect(() => {
    if (status === 'unknown') void bootstrap();
  }, [status, bootstrap]);

  useEffect(() => {
    if (status === 'authenticated' && !mustCreateAdmin) {
      navigate('/', { replace: true });
    }
  }, [status, mustCreateAdmin, navigate]);

  const submitLogin = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login(username, password);
    } catch (cause) {
      setError(describeError(cause));
    } finally {
      setBusy(false);
    }
  };

  const submitSetup = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await setupAdmin({ username: newUsername, password: newPassword });
      navigate('/', { replace: true });
    } catch (cause) {
      setError(describeError(cause));
    } finally {
      setBusy(false);
    }
  };

  const setupRequired = status === 'authenticated' && mustCreateAdmin;

  return (
    <Box
      className="app-shell"
      sx={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        bgcolor: 'background.default',
        p: 2,
      }}
    >
      {setupRequired ? (
        <Paper
          className="page-enter"
          sx={{ p: 4, width: '100%', maxWidth: 400 }}
          component="form"
          onSubmit={submitSetup}
        >
          <Stack spacing={2.5}>
            <Box>
              <Typography variant="h5">Create the real admin</Typography>
              <Typography variant="body2" color="text.secondary">
                You are signed in with the default account seeded on first boot.
                Create a real admin to continue — the default is removed for good
                once you do.
              </Typography>
            </Box>

            {error && <Alert severity="error">{error}</Alert>}

            <TextField
              label="Admin username"
              value={newUsername}
              onChange={(e) => setNewUsername(e.target.value)}
              autoComplete="username"
              autoFocus
              required
              fullWidth
            />
            <TextField
              label="Admin password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              helperText="At least 8 characters."
              autoComplete="new-password"
              required
              fullWidth
            />

            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={busy}
              fullWidth
            >
              {busy ? 'Creating…' : 'Create admin'}
            </Button>
          </Stack>
        </Paper>
      ) : (
        <Paper
          className="page-enter"
          sx={{ p: 4, width: '100%', maxWidth: 400 }}
          component="form"
          onSubmit={submitLogin}
        >
          <Stack spacing={2.5}>
            <Box>
              <Typography variant="h5">Bale Bot Admin</Typography>
              <Typography variant="body2" color="text.secondary">
                Sign in to manage routing and settings.
              </Typography>
            </Box>

            {error && <Alert severity="error">{error}</Alert>}

            <TextField
              label="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              autoFocus
              required
              fullWidth
            />
            <TextField
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
              fullWidth
            />

            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={busy}
              fullWidth
            >
              {busy ? 'Signing in…' : 'Sign in'}
            </Button>
          </Stack>
        </Paper>
      )}
    </Box>
  );
}